import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Wrapper from "../../../components/TenantWrapper";
import title from "../../../components/title";
import { GetTenantCurrentBill } from "../../../redux/reducer/GetTenantCurrentBill";
import { useOnlineBillPayment } from "../CurrentBills/useOnlineBillPayment";

// "Pay Rent" — rent is one of the tenant's monthly bills, so this page lists the
// rent bills that are still unpaid (this month's and any older ones) and pays
// them through the same bill system as everything else. It replaces the old
// rent-collection page, which raised its own second "rent payment" for the same
// month.

const rs = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;

export default function TenantRentBills() {
  title("Pay Rent");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.sidebar.open);
  const tenantProfile = useSelector(
    (state) => state.validateUser?.UserValidate?.data?.tenantProfile
  );
  const flat = tenantProfile?.propertyDetail?.[0];
  const { startPayment, loadingId, dialog } = useOnlineBillPayment();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!flat?.landlordId || !flat?._id) {
      setLoading(false);
      return;
    }
    dispatch(GetTenantCurrentBill({ id: `${flat.landlordId}/${flat._id}` }))
      .then((res) => setPayload(res?.payload?.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, flat?.landlordId, flat?._id]);

  // This cycle's rent bill (if unpaid) followed by older unpaid rent, oldest first.
  const rentBills = useMemo(() => {
    if (!payload) return [];
    const rows = [];

    (payload.previousUnpaid || [])
      .filter((r) => r.key === "rent")
      .forEach((r) =>
        rows.push({
          bill: r.bill,
          label: r.cycleKey,
          dueDate: r.dueDate,
          base: r.baseAmount,
          penalty: r.penalty,
          daysOverdue: r.daysOverdue,
          rejected: r.approvalStatus === "rejected",
        })
      );

    const current = payload.data?.rent_data?.[0];
    const p = payload.penalties?.rent;
    if (payload.data?.rent && current && !p?.settled) {
      rows.push({
        bill: current,
        label: current.cycleKey || payload.cycle?.cycleKey,
        dueDate: current.dueDate,
        base: p?.baseAmount ?? Number(current.billAmount) ?? 0,
        penalty: p?.penalty || 0,
        daysOverdue: p?.daysOverdue || 0,
        rejected: current.approval?.status === "rejected",
      });
    }
    return rows;
  }, [payload]);

  const total = rentBills.reduce((sum, r) => sum + r.base + r.penalty, 0);

  return (
    <>
      <Wrapper open={open} mylocation="/rent-payment" />
      <Box className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Box sx={{ maxWidth: 820, mx: "auto", p: { xs: 2, md: 4 } }}>
          <Typography variant="h5" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <HomeIcon color="primary" /> Pay Rent
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {flat?.flatName ? `${flat.flatName} — ` : ""}rent bills waiting to be paid.
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rentBills.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {payload?.cycle?.awaitingFirstCycle
                ? payload.cycle.emptyStateMessage
                : "You have no outstanding rent. It will appear here once the next bill is issued."}
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                Total rent due: <strong>{rs(total)}</strong>
                {rentBills.length > 1 ? ` (${rentBills.length} months)` : ""}
              </Alert>
              {rentBills.map((r) => (
                <Paper key={r.bill._id} elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: "1px solid #e5e7eb" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                    <Box>
                      <Typography fontWeight={800}>Rent · {r.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Due {r.dueDate || "—"}
                        {r.rejected ? " · previous payment was rejected" : ""}
                      </Typography>
                      {r.daysOverdue > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          sx={{ mt: 0.75 }}
                          label={`${r.daysOverdue} day${r.daysOverdue === 1 ? "" : "s"} late${
                            r.penalty > 0 ? ` · late fee ${rs(r.penalty)}` : ""
                          }`}
                        />
                      )}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="h6" fontWeight={800}>
                        {rs(r.base + r.penalty)}
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ mt: 1 }}
                        disabled={loadingId === r.bill._id}
                        onClick={() => startPayment("rent", r.bill)}
                      >
                        {loadingId === r.bill._id ? "Please wait…" : "Pay online"}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </>
          )}

          <Button sx={{ mt: 2 }} onClick={() => navigate(flat ? `/current-Bill/${flat.landlordId}/${flat._id}` : "/tenant-dashboard")}>
            All my bills
          </Button>
        </Box>
      </Box>
      {dialog}
    </>
  );
}
